import { IUGCReview } from '@interfaces';
import { IRequestType } from '@interfaces/request';
import React from 'react';
import StarSvg from '../../../../public/static/icons/star.svg';
import ThumbsUpSvg from '../../../../public/static/icons/thumbsUp.svg';
import UserGroupSvg from '../../../../public/static/icons/user-group.svg';
import { DeviceContext } from '../../../contexts/device/device';
import { theme } from '../../../style/global-style';
import { eventTracker } from '../../../utils/EventTracker';
import { numberFormat } from '../../../utils/formatting';
import { __ } from '../../../utils/translation-service';
import { DreamlinesButton } from '../../atoms/dreamlines-button/dreamlines-button';
import { Highlightbar } from '../../atoms/highlightbar/highlightbar';
import { IProgressbar, Progressbar } from '../../atoms/progressbar/progressbar';
import { Rating } from '../../atoms/rating/rating';
import { SectionHeadline } from '../../atoms/section-headline/section-headline';
import { ITestimonial } from '../../molecules/testimonial-slider/testimonial';
import { TestimonialSlider } from '../../molecules/testimonial-slider/testimonial-slider';
import UgcReviews from '../ugc-reviews/ugc-reviews';
import {
    StyledBarContainer,
    StyledBarSection,
    StyledButtonWrapper,
    StyledComponentWrapper,
    StyledDetailContainer,
    StyledHighlightContainer,
    StyledHighlightList,
    StyledHighlightListItem,
    StyledOverviewContainer,
    StyledRatingInner,
    StyledRatingWrapper,
    StyledTestimonialContainer,
} from './ugc.style';

export interface IUGCProps {
    headline: string;
    title: string;
    totalRatings: number;
    ratingRanges?: IProgressbar[];
    detailRatings?: IProgressbar[];
    ratingValue?: number;
    ratingString: string;
    testimonials: ITestimonial[];
    requestType: IRequestType;
    href?: string;
    buttonText?: string;
    reviews?: IUGCReview[];
    onShowMore?: (offset: number, limit: number) => Promise<IUGCReview[]>;
}

export const UGC = ({
    title,
    headline,
    totalRatings,
    ratingRanges,
    detailRatings,
    ratingValue,
    ratingString,
    testimonials,
    href,
    buttonText,
    reviews,
    onShowMore,
}: Partial<IUGCProps>) => {
    const detectedDevice = React.useContext(DeviceContext);

    const secondaryDetailRatings = detailRatings
        ? detectedDevice === 'desktop'
            ? detailRatings
            : detailRatings.slice(0, 5)
        : [];

    if (!totalRatings || totalRatings === 0) {
        return null;
    }

    const nonEmptyTestimonials = testimonials?.filter((item) => Boolean(item.quote)) || [];

    return (
        <StyledComponentWrapper>
            <SectionHeadline
                headerType={reviews ? 'h1' : 'h2'}
                firstChunk={title || ''}
                secondChunk={headline + ` (${numberFormat(totalRatings)})`}
                paddingBottom={10}
            />

            <StyledHighlightContainer>
                <Highlightbar headline={__('ugcUSPHeadline')}>
                    <StyledHighlightList>
                        <StyledHighlightListItem align={'flex-start'}>
                            <div>
                                <UserGroupSvg /> <span>{__('ugcUSP1')}</span>
                            </div>
                        </StyledHighlightListItem>
                        <StyledHighlightListItem align={'center'}>
                            <div>
                                <ThumbsUpSvg /> <span>{__('ugcUSP2')}</span>
                            </div>
                        </StyledHighlightListItem>
                        <StyledHighlightListItem align={'flex-end'}>
                            <div>
                                <StarSvg /> <span>{__('ugcUSP3')}</span>
                            </div>
                        </StyledHighlightListItem>
                    </StyledHighlightList>
                </Highlightbar>
            </StyledHighlightContainer>

            <StyledRatingWrapper>
                {!!ratingValue && ratingString && (
                    <StyledRatingInner>
                        <Rating
                            value={ratingValue}
                            string={ratingString}
                            amount={totalRatings}
                            size="L"
                        />
                    </StyledRatingInner>
                )}

                {!!nonEmptyTestimonials.length && (
                    <StyledTestimonialContainer>
                        <TestimonialSlider testimonials={nonEmptyTestimonials} />
                    </StyledTestimonialContainer>
                )}
            </StyledRatingWrapper>
            {ratingRanges && ratingRanges.length > 0 && (
                <StyledBarSection>
                    <StyledOverviewContainer>
                        {ratingRanges?.map((rating) => (
                            <StyledBarContainer key={rating.description}>
                                <Progressbar
                                    value={rating.value}
                                    base={totalRatings}
                                    description={rating.description}
                                    showBase={false}
                                    descriptionColor={theme.colors['main4']}
                                />
                            </StyledBarContainer>
                        ))}
                    </StyledOverviewContainer>
                    <StyledDetailContainer>
                        {secondaryDetailRatings.map((rating) => (
                            <StyledBarContainer key={rating.description}>
                                <Progressbar
                                    value={rating.value}
                                    description={rating.description}
                                />
                            </StyledBarContainer>
                        ))}
                    </StyledDetailContainer>
                </StyledBarSection>
            )}
            {href && buttonText && (
                <StyledButtonWrapper arrow="right">
                    <DreamlinesButton
                        tertiary
                        style={{
                            color: theme.colors.main4,
                            borderColor: theme.colors.main4,
                            backgroundColor: 'transparent',
                        }}
                        redirectUrl={href}
                        onClick={() =>
                            eventTracker.track({
                                event: 'click',
                                category: 'link-to-subpage',
                                action: 'ugc-module',
                                label: `ship-${title}`,
                            })
                        }
                    >
                        {buttonText}
                    </DreamlinesButton>
                </StyledButtonWrapper>
            )}
            {reviews && onShowMore && (
                <UgcReviews reviews={reviews} totalCount={totalRatings} onShowMore={onShowMore} />
            )}
        </StyledComponentWrapper>
    );
};
